import { useSubscriptionV2 } from "@web-src/modules/cx/providers/SubscriptionV2Provider";
import { useEntityProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntityProvider";
import { useMemo } from "react";

export type ShopifySubscriptionGateState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "blocked"; openShopifyPricingPage: () => void }
  | { status: "allowed" };

/**
 * Gates Shopify marketplace-created workspaces until subscription is usable.
 */
export const useShopifySubscriptionGate = (): ShopifySubscriptionGateState => {
  const { entity } = useEntityProvider();

  const {
    isLoading,
    liteSubscription,
    subscription,
    isShopifyBilling,
    isShopifySubscriptionPending,
    openShopifyPricingPage,
  } = useSubscriptionV2();

  const state = useMemo<ShopifySubscriptionGateState>(() => {
    // Non-Shopify workspaces are unaffected
    if (!entity || entity.type !== "cx" || !isShopifyBilling) {
      return { status: "idle" };
    }

    // Inactive Shopify payload counts as loaded (pending), not still loading
    if (
      isLoading &&
      !isShopifySubscriptionPending &&
      !liteSubscription &&
      !subscription
    ) {
      return { status: "loading" };
    }

    const subscriptionStatus = subscription?.status ?? liteSubscription?.status;

    // Active subscriptions can use the app. Canceled (e.g. app uninstalled
    // from Shopify while the workspace remains) should also be allowed in —
    // feature access is gated separately via isSubscriptionCanceled.
    if (subscriptionStatus === "active" || subscriptionStatus === "canceled") {
      return { status: "allowed" };
    }

    return {
      status: "blocked",
      openShopifyPricingPage,
    };
  }, [
    entity,
    isLoading,
    isShopifyBilling,
    isShopifySubscriptionPending,
    liteSubscription,
    openShopifyPricingPage,
    subscription,
  ]);

  return state;
};
